import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';

import { ErrorIcon } from './ErrorIcon';
import { ErrorWarningMessage } from './ErrorWarningMessage';
import { OptionalTooltip } from './OptionalTooltip';

import './InputGroup.scss';

type AllowedInputTypes = 'text' | 'email' | 'password' | 'tel';
type InputTypes = Extract<React.HTMLInputTypeAttribute, AllowedInputTypes>

type Optional = {
  optional: true
  optionalTooltip?: string
} | {
  optional?: false
  optionalTooltip?: never
}

type InputGroupProps = {
  autoComplete?: string
  autoFocus?: boolean
  disabled?: boolean
  id: string
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode']
  label: string
  minLength?: React.InputHTMLAttributes<HTMLInputElement>['minLength']
  maxLength?: React.InputHTMLAttributes<HTMLInputElement>['maxLength']
  name: string
  pattern?: React.InputHTMLAttributes<HTMLInputElement>['pattern']
  placeholder?: string
  required?: React.InputHTMLAttributes<HTMLInputElement>['required']
  type: InputTypes
  value: string

  error?: string | null
  warning?: string | null

  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
} & Optional

export function InputGroup(props: InputGroupProps): React.JSX.Element {
  const [showPasswordText, setShowPasswordText] = useState(false);

  function getType(type: InputTypes): InputTypes {
    if (type === 'password') {
      return showPasswordText ? 'text' : 'password';
    }

    return type;
  }

  function handleTogglePassword(): void {
    setShowPasswordText(!showPasswordText);
  }

  return (
    <div className={`form-field ${(props.error || props.warning) ? 'invalid' : ''}`}>
      <ErrorWarningMessage error={props.error} warning={props.warning} />

      <OptionalTooltip tooltip={props.optionalTooltip} />

      <label className="form-field__label">
        <input
          className="form-field__input"
          id={props.id}
          name={props.name}
          type={getType(props.type)}
          value={props.value}
          placeholder={props.placeholder}

          onChange={props.onChange}

          autoComplete={props.autoComplete}
          autoFocus={props.autoFocus}
          disabled={props.disabled}
          inputMode={props.inputMode}
          minLength={props.minLength}
          maxLength={props.maxLength}
          pattern={props.pattern}
          required={props.required}
        />
        <span className={`${props.optional ? 'optional-label' : ''}`}>{props.label}</span>

        {props.type === 'password' && (
          <div
            className={`form-field__password form-field__svg-icon form-field__svg-icon${props.error ? '--password' : ''}`}
            data-testid='password-show-hide-icon-wrapper'
          >
            <FontAwesomeIcon
              onClick={handleTogglePassword}
              icon={showPasswordText ? faEyeSlash : faEye}
              size="1x"
              data-testid='password-show-hide-icon'
            />
          </div>
        )}

        <ErrorIcon error={props.error} optional={props.optional} />
      </label>
    </div>
  );
}
